import { ensureThemeText } from 'builder/util/resolveTheme';
import {
  horizonOptumTheme,
  horizonRallyTheme,
  horizonUhcTheme,
  darkTheme,
} from 'builder/scenes/SiteBuilder/SiteStylesPanel/BrandThemes';

// Lightweight Theme type compatible with BrandThemes
type ThemeLike = {
  font?: any;
  colors: {
    primary: string;
    secondary: string;
    tertiary: string;
    quaternary?: string;
    text?: { primary: string; secondary: string };
    custom?: Array<{ id: string; value: string }>;
    aux?: Record<string, string>;
  };
};

// Retain brand imports for potential future use; not needed for resolver path

const byIdFromCustom = (theme: ThemeLike, id: string, fallback: string) => {
  const map: Record<string, string> = Object.fromEntries(
    (theme.colors?.custom ?? []).map(c => [c.id, c.value])
  );
  return map[id] ?? fallback;
};

/**
 * Build widget/page tokens from a theme key + site overrides.
 * Guarantees text colors via ensureThemeText().
 */
export const getTokensForWidgets = (themeKey: string, site: Site) => {
  // Start from brand base
  let base: ThemeLike = ((): ThemeLike => {
    switch (themeKey) {
      case 'optum':
        return (horizonOptumTheme as unknown) as ThemeLike;
      case 'uhc':
        return (horizonUhcTheme as unknown) as ThemeLike;
      case 'rally':
        return (horizonRallyTheme as unknown) as ThemeLike;
      case 'dark':
        return (darkTheme as unknown) as ThemeLike;
      default:
        return (horizonOptumTheme as unknown) as ThemeLike;
    }
  })();

  // Overlay with custom theme colors when applicable
  if (
    /^customTheme\d+$/.test(themeKey) &&
    (site?.theme as any)?.[themeKey]?.colors
  ) {
    const c = (site.theme as any)[themeKey].colors;
    base = {
      ...base,
      colors: {
        ...base.colors,
        primary: c.primary ?? base.colors.primary,
        secondary: c.secondary ?? base.colors.secondary,
        tertiary: c.tertiary ?? base.colors.tertiary,
        quaternary: c.quaternary ?? base.colors.quaternary,
        custom: c.custom ?? base.colors.custom,
      },
      font: (site.theme as any)?.font ?? (base as any).font,
    } as ThemeLike;
  }

  // Do not merge site.theme.colors here; mirror SiteStylesPanel behavior exactly

  // Normalize text colors
  base = (ensureThemeText(base as any) as unknown) as ThemeLike;

  const textPrimary = base.colors.text!.primary;
  const textSecondary = base.colors.text!.secondary;
  const font =
    base.font?.family?.base?.value ??
    (themeKey === 'uhc' ? 'UHCSans' : 'OptumSans');

  // Defaults for light (match SiteStylesPanel)
  let tokens = {
    font,
    textPrimary,
    textSecondary,
    pageBg: '#ffffff',
    surfaceBg: '#ffffff',
    link: base.colors?.primary ?? '#0C55B8',
    linkHover: base.colors?.tertiary ?? base.colors?.secondary ?? '#003A8D',
    hoverBg: base.colors?.tertiary ?? base.colors?.secondary ?? '#001D5B',
    buttonBg: base.colors?.primary ?? '#0C55B8',
    buttonHoverBg:
      base.colors?.quaternary ?? base.colors?.secondary ?? '#003A8D',
    buttonText: '#ffffff',
    buttonTextHover: '#ffffff',
  };

  if (themeKey === 'dark') {
    tokens = {
      ...tokens,
      pageBg: byIdFromCustom(base, 'pageBackground', '#121212'),
      surfaceBg: byIdFromCustom(base, 'cardBackground', '#1E1E1E'),
      link: byIdFromCustom(base, 'link', '#8AB4F8'),
      linkHover: byIdFromCustom(base, 'linkHover', '#AECBFA'),
      hoverBg: byIdFromCustom(base, 'buttonHover', '#985EFF'),
      buttonBg: byIdFromCustom(base, 'buttonBackground', '#BB86FC'),
      buttonHoverBg: byIdFromCustom(base, 'buttonHover', '#985EFF'),
      buttonText: byIdFromCustom(base, 'buttonText', '#000000'),
      buttonTextHover: byIdFromCustom(base, 'buttonTextHover', '#000000'),
      textPrimary,
      textSecondary,
    };
  }

  return tokens;
};
